// apps/app/src/coachmark/useCoachmarkSequence.ts
// Watches a set of game-state booleans (matchStarted, firstUnitTrained, …)
// and fires the next un-seen coachmark via the controller. Waits for the
// target data-attribute to land in the DOM before opening; skips silently
// when no target appears within 5s.

import { useEffect, useRef } from 'react';
import { type CoachmarkId, coachmarkContent } from './content.js';
import type { CoachmarkController } from './useCoachmarks.js';

export interface SequenceState {
  readonly controller: CoachmarkController;
  readonly matchStarted: boolean;
  readonly firstUnitTrained: boolean;
  readonly firstCombatResolved: boolean;
  readonly turnEnded: boolean;
  readonly matchEnded: boolean;
}

const TARGET_WAIT_MS = 5_000;

function waitForTarget(selector: string, abort: AbortSignal): Promise<Element | null> {
  const existing = document.querySelector(selector);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, TARGET_WAIT_MS);
    abort.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      observer.disconnect();
      resolve(null);
    });
  });
}

function triggerEventFor(key: keyof Omit<SequenceState, 'controller'>): string | null {
  switch (key) {
    case 'matchStarted':
      return 'match.started';
    case 'firstUnitTrained':
      return 'unit.trained';
    case 'firstCombatResolved':
      return 'combat.resolved';
    case 'turnEnded':
      return 'turn.ended';
    case 'matchEnded':
      return 'match.ended';
  }
  return null;
}

export function useCoachmarkSequence(state: SequenceState): void {
  const firedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fired = firedRef.current;
    const flags: (keyof Omit<SequenceState, 'controller'>)[] = [
      'matchStarted',
      'firstUnitTrained',
      'firstCombatResolved',
      'turnEnded',
      'matchEnded',
    ];
    const triggers = flags.map((f) => [f, state[f]] as const).filter(([, v]) => v);
    const controller = state.controller;

    const abortController = new AbortController();
    const openNext = async () => {
      for (const [flag, _] of triggers) {
        const trigger = triggerEventFor(flag);
        if (!trigger) continue;
        for (const id of Object.keys(coachmarkContent) as CoachmarkId[]) {
          const entry = coachmarkContent[id];
          if (entry.triggerEvent !== trigger) continue;
          if (controller.isSeen(id)) continue;
          // The audience gate, on the OPEN side: a hint whose hideWhenSelector
          // matches (start-game over a Continue button — a returning player)
          // must never open at all. Closing it after the fact leaves a
          // paint-sized flicker, which is exactly what a coverage probe catches.
          if (entry.hideWhenSelector && document.querySelector(entry.hideWhenSelector)) {
            fired.add(id); // refused for this session — never re-arm
            continue;
          }
          if (fired.has(id)) continue;
          const target = await waitForTarget(entry.targetSelector, abortController.signal);
          if (!target) break;
          if (controller.isOpen(id)) continue;
          fired.add(id);
          controller.openHint(id);
          return;
        }
      }
    };

    if (triggers.length > 0) {
      void openNext();
    }

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.matchStarted,
    state.firstUnitTrained,
    state.firstCombatResolved,
    state.turnEnded,
    state.matchEnded,
  ]);
}
